import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Twitter, ArrowRight, ArrowLeft, Calendar, Clock, Users, Heart, Share2, Mail, Radio, Lock, Bell, Star, Sparkles, Copy, Check, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useXSpaces } from "@/hooks/useXSpaces";
import { useFavoriteEvents } from "@/hooks/useFavoriteEvents";
import { useInteractionSounds } from "@/hooks/useInteractionSounds";
import { useAuth } from "@/hooks/useAuth";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";

// Custom icons
const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const calculateCountdown = (targetDate: Date): CountdownTime => {
  const now = new Date().getTime();
  const target = targetDate.getTime();
  const difference = target - now;
  
  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  
  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((difference % (1000 * 60)) / 1000)
  };
};

interface XSpacesSectionProps {
  isRTL: boolean;
  limit?: number;
}

const SpaceCard = ({ space, isRTL }: { space: any; isRTL: boolean }) => {
  const { i18n } = useTranslation();
  const { toggleFavorite, isFavorite } = useFavoriteEvents();
  const { playHoverSound, playFavoriteSound, playShareSound } = useInteractionSounds();
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const title = space.title?.[i18n.language as 'en' | 'ar'] || space.title?.en || '';
  const shareUrl = `https://visitevent.live/x-space/${space.dbId}`;
  const shareMessage = isRTL 
    ? `🎙️ بث مباشر على منصة X!\n\n📢 ${title}\n👥 ${space.attendees || 0} مشارك\n\n🔗 سجل الآن للانضمام:`
    : `🎙️ Live broadcast on X Platform!\n\n📢 ${title}\n👥 ${space.attendees || 0} participants\n\n🔗 Register now to join:`;
  const encodedShareMessage = encodeURIComponent(shareMessage);
  const encodedShareUrl = encodeURIComponent(shareUrl);

  const registrationBenefits = [
    {
      icon: Twitter,
      title: isRTL ? "الانضمام للمساحات" : "Join Spaces",
      description: isRTL ? "شارك في مساحات X المباشرة" : "Participate in live X Spaces"
    },
    {
      icon: Heart,
      title: isRTL ? "حفظ المفضلة" : "Save Favorites",
      description: isRTL ? "احفظ المساحات المفضلة" : "Save your favorite spaces"
    },
    {
      icon: Bell,
      title: isRTL ? "التذكيرات" : "Reminders",
      description: isRTL ? "تنبيهات قبل البث" : "Get notified before broadcasts"
    },
    {
      icon: Star,
      title: isRTL ? "مميزات حصرية" : "Exclusive Features",
      description: isRTL ? "محتوى مميز للأعضاء" : "Premium content for members"
    }
  ];

  const handleLoginPromptNavigate = useCallback(() => {
    setIsLoginPromptOpen(false);
    navigate("/auth");
  }, [navigate]);

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      setIsLoginPromptOpen(true);
      return;
    }
    playFavoriteSound(!isFavorite(space.id));
    
    if (!isFavorite(space.id)) {
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.7 },
        colors: ['#ff6b6b', '#ee5a5a', '#ff8787', '#ffc9c9'],
        shapes: ['circle'],
      });
    }
    
    await toggleFavorite(space.id, isRTL);
  };

  const handleJoinClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) {
      setIsLoginPromptOpen(true);
      return;
    }
    window.open(space.xSpaceUrl, "_blank");
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    playShareSound(!showShareMenu);
    setShowShareMenu(!showShareMenu);
    setCopied(false);
  };

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success(isRTL ? "تم نسخ الرابط" : "Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <>
      {/* Login Prompt Dialog */}
      <Dialog open={isLoginPromptOpen} onOpenChange={setIsLoginPromptOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 rounded-3xl" style={{
          background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
          boxShadow: '20px 20px 60px #d1d1d1, -20px -20px 60px #ffffff'
        }}>
          <div className="relative bg-gradient-to-br from-sky-500 to-blue-600 p-6 text-white text-center">
            <div className="absolute inset-0 bg-black/10" />
            <motion.div 
              initial={{ scale: 0 }} 
              animate={{ scale: 1 }} 
              transition={{ type: "spring", delay: 0.1 }}
              className="relative"
            >
              <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-1">
                {isRTL ? "انضم إلينا!" : "Join Us!"}
              </h3>
              <p className="text-white/80 text-sm">
                {isRTL ? "سجل للانضمام للمساحات" : "Register to join spaces"}
              </p>
            </motion.div>
          </div>

          <div className="p-5 space-y-3">
            {registrationBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="flex items-center gap-4 p-3 rounded-2xl"
                style={{
                  background: 'linear-gradient(145deg, #f8f8f8, #ffffff)',
                  boxShadow: '3px 3px 8px #e8e8e8, -3px -3px 8px #ffffff'
                }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-sky-100">
                  <benefit.icon className="w-5 h-5 text-sky-600" />
                </div>
                <div className={isRTL ? "text-right" : "text-left"}>
                  <h4 className="font-semibold text-gray-700 text-sm">{benefit.title}</h4>
                  <p className="text-xs text-gray-500">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="p-5 pt-2 space-y-3">
            <Button 
              onClick={handleLoginPromptNavigate}
              className="w-full py-6 rounded-2xl text-base font-semibold bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 transition-all"
              style={{
                boxShadow: '5px 5px 15px #c0c0c0, -5px -5px 15px #ffffff'
              }}
            >
              {isRTL ? "سجل الآن مجاناً" : "Register Now - Free"}
            </Button>
            <button 
              onClick={() => setIsLoginPromptOpen(false)}
              className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              {isRTL ? "لاحقاً" : "Maybe Later"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <motion.div
      className="relative bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl overflow-hidden group"
      style={{
        boxShadow: '8px 8px 16px rgba(0,0,0,0.1), -8px -8px 16px rgba(255,255,255,0.8)',
      }}
      whileHover={{ y: -5, scale: 1.02 }}
      onMouseEnter={() => playHoverSound()}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Action Buttons */}
      <div className="absolute top-3 left-3 z-20 flex gap-2">
        <motion.button
          onClick={handleFavoriteClick}
          className={`p-2 rounded-full backdrop-blur-sm transition-all ${
            isFavorite(space.id)
              ? 'bg-red-500 text-white'
              : 'bg-white/80 text-gray-600 hover:bg-red-100 hover:text-red-500'
          }`}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <Heart className={`w-4 h-4 ${isFavorite(space.id) ? 'fill-current' : ''}`} />
        </motion.button>
        
        <div className="relative">
          <motion.button
            onClick={handleShareClick}
            className="p-2 rounded-full bg-white/80 text-gray-600 hover:bg-blue-100 hover:text-blue-500 backdrop-blur-sm transition-all"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <Share2 className="w-4 h-4" />
          </motion.button>
          
          <AnimatePresence>
            {showShareMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -10 }}
                className="absolute top-full left-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl p-2 flex gap-2 z-30"
              >
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodedShareMessage}&url=${encodedShareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <XIcon />
                </a>
                <a
                  href={`https://t.me/share/url?url=${encodedShareUrl}&text=${encodedShareMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-blue-500"
                  onClick={(e) => e.stopPropagation()}
                >
                  <TelegramIcon />
                </a>
                <a
                  href={`https://wa.me/?text=${encodedShareMessage}%20${encodedShareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-green-500"
                  onClick={(e) => e.stopPropagation()}
                >
                  <WhatsAppIcon />
                </a>
                <a
                  href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodedShareMessage}%20${encodedShareUrl}`}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-600"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Mail className="w-4 h-4" />
                </a>
                <button
                  onClick={handleCopyLink}
                  className={`p-2 rounded-full transition-colors ${copied ? 'bg-green-100 text-green-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600'}`}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Image */}
      <div className="relative h-40 overflow-hidden">
        <img 
          src={space.image || "/images/x-spaces-default.jpg"} 
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "/images/x-spaces-default.jpg";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute top-3 right-3 bg-gray-900 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
          <Twitter className="w-3 h-3" />
          X Space
        </div>
        {/* Status Badge - Right Side */}
        {space.isLive && (
          <div className="absolute bottom-3 right-3 bg-red-600 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 animate-pulse shadow-lg">
            <Radio className="w-3.5 h-3.5" />
            {isRTL ? "بث مباشر" : "LIVE NOW"}
          </div>
        )}
        {space.isEnded && !space.isLive && (
          <div className="absolute bottom-3 right-3 bg-gray-600 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg">
            <CheckCircle className="w-3.5 h-3.5" />
            {isRTL ? "منتهية" : "ENDED"}
          </div>
        )}
        {/* Attendees Badge - Left Side (show for live and ended spaces too) */}
        {space.attendees > 0 && (
          <div className="absolute bottom-3 left-3 bg-white/90 text-gray-800 px-2 py-1 rounded-full text-xs flex items-center gap-1 shadow-md">
            <Users className="w-3 h-3" />
            {space.attendees}
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h3 className="font-bold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {title}
        </h3>
        
        <div className="space-y-2 text-sm text-muted-foreground mb-4">
          {space.date && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{space.date[i18n.language as 'en' | 'ar']}</span>
            </div>
          )}
          {space.startTime && (
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>
                {space.startTime[i18n.language as 'en' | 'ar']}
                {space.endTime && ` - ${space.endTime[i18n.language as 'en' | 'ar']}`}
              </span>
            </div>
          )}
        </div>
        
        {space.xSpaceUrl && (
          user ? (
            <a 
              href={space.xSpaceUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary hover:underline text-sm font-medium"
            >
              <Twitter className="w-4 h-4" />
              {isRTL ? "انضم للمساحة" : "Join Space"}
            </a>
          ) : (
            <button 
              onClick={handleJoinClick}
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
            >
              <Lock className="w-4 h-4" />
              {isRTL ? "سجل للانضمام" : "Login to Join"}
            </button>
          )
        )}
      </div>
    </motion.div>
    </>
  );
};

// Add Your Voice Space Card
const AddSpaceCard = ({ isRTL }: { isRTL: boolean }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { playHoverSound } = useInteractionSounds();

  const handleClick = () => {
    if (!user) {
      navigate("/auth");
    } else {
      navigate("/packages");
    }
  };

  return (
    <motion.div
      className="relative bg-gradient-to-br from-sky-50 to-blue-100 dark:from-sky-900/20 dark:to-blue-900/30 rounded-xl overflow-hidden cursor-pointer border-2 border-dashed border-sky-300 dark:border-sky-600 min-h-[320px] flex items-center justify-center"
      style={{
        boxShadow: '8px 8px 16px rgba(0,0,0,0.08), -8px -8px 16px rgba(255,255,255,0.9)',
      }}
      whileHover={{ y: -5, scale: 1.02 }}
      onMouseEnter={() => playHoverSound()}
      onClick={handleClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="text-center p-6">
        <motion.div 
          className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center shadow-lg"
          whileHover={{ rotate: 10 }}
        >
          <Twitter className="w-10 h-10 text-white" />
        </motion.div>
        <h3 className="text-xl font-bold text-sky-700 dark:text-sky-300 mb-2">
          {isRTL ? "أضف مساحتك الصوتية" : "Add Your Voice Space"}
        </h3>
        <p className="text-sm text-sky-600/70 dark:text-sky-400/70 mb-4">
          {isRTL ? "شارك مساحتك مع الآلاف" : "Share your space with thousands"}
        </p>
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-full text-sm font-medium shadow-md">
          {isRTL ? "ابدأ الآن" : "Start Now"}
          {isRTL ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
        </div>
      </div>
    </motion.div>
  );
};

export const XSpacesSection = ({ isRTL, limit = 6 }: XSpacesSectionProps) => {
  const { data: xSpaceEvents = [], isLoading } = useXSpaces(limit);
  const { i18n } = useTranslation();

  if (isLoading) {
    return (
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4 mb-10">
            <div className="p-3 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow-lg">
              <Twitter className="w-6 h-6" />
            </div>
            <h2 className="text-2xl font-bold">
              {isRTL ? "مساحات X" : "X Spaces"}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-64 bg-gray-200 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (xSpaceEvents.length === 0) {
    return null;
  }

  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow-lg`}>
              <Twitter className="w-6 h-6" />
            </div>
            <h2 className="text-2xl font-bold">
              {isRTL ? "مساحات X" : "X Spaces"}
            </h2>
          </div>
          
          <Link to="/x-spaces">
            <Button variant="outline" className="gap-2 group">
              {isRTL ? "عرض الكل" : "View All"}
              {isRTL ? (
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              ) : (
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              )}
            </Button>
          </Link>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Add Your Space Card */}
          <AddSpaceCard isRTL={isRTL} />
          
          {xSpaceEvents.map((space) => (
            <SpaceCard key={space.id} space={space} isRTL={isRTL} />
          ))}
        </div>
      </div>
    </section>
  );
};
